/**
 * auth-api.adapter — implementation ของ `AuthApiPort`
 *
 * 🔴 **ทุก endpoint ที่นี่ต้องส่ง `credentials: 'include'`**
 *    refresh token มาทาง httpOnly cookie ที่ backend ตั้งบน `.theeliteinc.co`
 *    (Auth doc §3.5) · ไม่ใส่ = เบราว์เซอร์ทิ้ง cookie ข้าม origin (`app.` → `api.`)
 *    แล้วอาการคือ **"login ได้แต่ refresh ไม่ได้"** ซึ่งโผล่อีกทีตอน access token
 *    หมดอายุ 15 นาทีต่อมา — หายากมาก
 *    ⇒ อยู่ในตัวกลาง `post()` ที่เดียว **endpoint ใหม่ลืมไม่ได้**
 *
 * ⚠️ **ต่างจาก `activities-api.adapter.ts` ตรงนี้พอดี — อย่าก๊อปทับกัน**
 *    ตัวนั้นห้ามแนบ cookie/token กับ 2 endpoint สาธารณะเด็ดขาด
 *
 * ℹ️ `HttpError` ของ `shared-web` กับ `AuthApiError` ของ feature เป็นรูปเดียวกัน
 *    (`reason` · `message` · `rule?`) ⇒ ส่งต่อได้ตรง ๆ แบบ structural **โดยไม่มีใคร
 *    import ใคร** · วันที่ 2 ฝั่งต่างกันจริง TypeScript จะฟ้องที่ไฟล์นี้ ซึ่งเป็นที่ที่ควรฟ้อง
 */
import type { AuthApiPort, SessionResult } from '@theelite/frontend-member-feature-auth';
import { createHttpClient } from '@theelite/frontend-shared-web';

export function createAuthApi(baseUrl: string): AuthApiPort {
  const http = createHttpClient(baseUrl);

  const post = async <T>(path: string, body: unknown, fallbackMessage: string) =>
    http.request<T>({
      method: 'POST',
      path,
      body,
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      fallbackMessage,
    });

  /**
   * LINE กับ Google ใช้รูปคำขอเดียวกันเป๊ะ (`{ idToken }` → `SessionResponse`)
   * — ต่างแค่ path · ก๊อป 2 ก้อนแล้ววันหนึ่งจะแก้ที่เดียวแล้วลืมอีกที่
   */
  const authenticate = async (provider: 'line' | 'google', idToken: string) => {
    const result = await post<SessionResult>(
      `/auth/${provider}`,
      { idToken },
      'เข้าสู่ระบบไม่สำเร็จ',
    );
    return result.ok ? ({ ok: true, session: result.data } as const) : result;
  };

  return {
    authenticateWithLine: (idToken) => authenticate('line', idToken),
    authenticateWithGoogle: (idToken) => authenticate('google', idToken),

    async verifyEmail(token) {
      const result = await post<{ memberId: string }>(
        '/auth/verify-email',
        { token },
        'ยืนยันอีเมลไม่สำเร็จ',
      );
      return result.ok ? ({ ok: true, memberId: result.data.memberId } as const) : result;
    },
  };
}
